import { AutocompleteProvider } from "../../../types/enums";
import { LatLng } from "../../maps/map-provider.interface";
import {
    AddressSearchMode,
    AddressSuggestOptions,
    AddressSuggestion,
    GeoAddress,
    IAddressSuggestProvider,
    IGeocodingProvider
} from "../address-search-provider.interface";
import { fetchJsonAsync } from "../geo-search-fetch";
import { toStreetShortName } from "../street-short-name";
import { MapboxFeature, MapboxFeatureCollection, MapboxFeatureProperties } from "./mapbox-api.interface";

const FORWARD_URL = "https://api.mapbox.com/search/geocode/v6/forward";
const REVERSE_URL = "https://api.mapbox.com/search/geocode/v6/reverse";

// Mapbox filters by type before applying the limit, so unlike HERE autosuggest the list fills
// without over-fetching.
const ADDRESS_FEATURE_TYPES = ["address", "street"];
const LOCALITY_FEATURE_TYPES = [
    ...ADDRESS_FEATURE_TYPES,
    "postcode",
    "place",
    "locality",
    "neighborhood",
    "district",
    "region"
];

/**
 * Geocoding v6 returns coordinates and address components with each suggestion, so selection needs
 * no second call, and the same API serves geocoding and reverse geocoding.
 */
export class MapboxAddressProvider implements IAddressSuggestProvider, IGeocodingProvider {
    public readonly providerId = AutocompleteProvider.MAPBOX;

    private readonly _accessToken: string;

    constructor(accessToken: string) {
        this._accessToken = accessToken;
    }

    public async suggestAsync(
        query: string,
        options: AddressSuggestOptions,
        signal: AbortSignal
    ): Promise<AddressSuggestion[]> {
        const url = this._createForwardUrl(query);
        url.searchParams.set("limit", String(options.limit));
        url.searchParams.set("types", this._getFeatureTypes(options.mode).join(","));

        this._appendSearchArea(url, options);

        const response = await fetchJsonAsync<MapboxFeatureCollection>(
            this.providerId,
            url.toString(),
            { signal }
        );

        return (response.features ?? [])
            .map(feature => this._toSuggestion(feature))
            .filter((suggestion): suggestion is AddressSuggestion => suggestion !== null);
    }

    private _getFeatureTypes(mode: AddressSearchMode): string[] {
        return mode === "address" ? ADDRESS_FEATURE_TYPES : LOCALITY_FEATURE_TYPES;
    }

    private _appendSearchArea(url: URL, options: AddressSuggestOptions): void {
        if (options.at)
            url.searchParams.set("proximity", `${options.at.lng},${options.at.lat}`);

        if (options.language)
            url.searchParams.set("language", options.language);

        const countryCodes = this._normalizeCountryCodes(options.countryCodes ?? []);
        if (countryCodes.length > 0)
            url.searchParams.set("country", countryCodes.join(","));
    }

    // Mapbox rejects the whole request over one malformed code, so blanks and anything that is not
    // alpha-2 are dropped and the search widens instead.
    private _normalizeCountryCodes(countryCodes: readonly string[]): string[] {
        const validCodes = countryCodes
            .map(code => code?.trim().toLowerCase())
            .filter(code => /^[a-z]{2}$/.test(code));

        return Array.from(new Set(validCodes));
    }

    // Every suggestion is resolved inline, so the chain never needs to call this.
    public resolveAsync(suggestion: AddressSuggestion): Promise<GeoAddress | null> {
        return Promise.resolve(suggestion.address ?? null);
    }

    public async geocodeAsync(query: string): Promise<LatLng | null> {
        const trimmed = query.trim();
        if (!trimmed)
            return null;

        const url = this._createForwardUrl(trimmed);
        url.searchParams.set("limit", "1");

        // Autocomplete matching is for partial input; a complete address places more precisely without it.
        url.searchParams.set("autocomplete", "false");

        const response = await fetchJsonAsync<MapboxFeatureCollection>(this.providerId, url.toString());

        const feature = response.features?.[0];
        return feature ? this._getCoords(feature) : null;
    }

    public async reverseGeocodeAsync(coords: LatLng): Promise<GeoAddress | null> {
        const url = new URL(REVERSE_URL);
        url.searchParams.set("access_token", this._accessToken);
        url.searchParams.set("longitude", String(coords.lng));
        url.searchParams.set("latitude", String(coords.lat));

        // No limit: Mapbox only accepts one alongside a single type, and without it the most
        // specific feature already comes first.
        const response = await fetchJsonAsync<MapboxFeatureCollection>(this.providerId, url.toString());

        const properties = response.features?.[0]?.properties;
        if (!properties)
            return null;

        return this._toGeoAddress(coords, properties);
    }

    private _createForwardUrl(query: string): URL {
        const url = new URL(FORWARD_URL);
        url.searchParams.set("access_token", this._accessToken);
        url.searchParams.set("q", query);

        return url;
    }

    private _toSuggestion(feature: MapboxFeature): AddressSuggestion | null {
        const properties = feature.properties;
        const label = properties?.full_address ?? properties?.name;
        const coords = this._getCoords(feature);
        if (!properties || !label || !coords)
            return null;

        return {
            label,
            providerId: this.providerId,
            address: this._toGeoAddress(coords, properties)
        };
    }

    private _getCoords(feature: MapboxFeature): LatLng | null {
        const [lng, lat] = feature.geometry?.coordinates ?? [];
        if (lng === undefined || lat === undefined)
            return null;

        return { lat, lng };
    }

    private _toGeoAddress(coords: LatLng, properties: MapboxFeatureProperties): GeoAddress {
        const context = properties.context ?? {};

        // The context may leave out the feature's own level, so a street, place or postcode
        // result falls back to its own name.
        const getOwnName = (featureType: string): string | undefined =>
            properties.feature_type === featureType ? properties.name : undefined;

        const street = context.address?.street_name ?? context.street?.name ?? getOwnName("street");

        return {
            coords,
            label: properties.full_address,
            countryCode: context.country?.country_code?.toUpperCase(),
            countryName: context.country?.name,
            stateCode: context.region?.region_code,
            stateName: context.region?.name,
            city: context.place?.name ?? getOwnName("place") ?? context.locality?.name ?? context.district?.name,
            postalCode: context.postcode?.name ?? getOwnName("postcode"),
            street,
            streetShortName: toStreetShortName(street),
            houseNumber: context.address?.address_number,
            district: context.neighborhood?.name,
            sublocality: context.locality?.name
        };
    }
}
